/**
 * 時間軸計算：日期 ⇄ 像素、刻度產生、非工作日判定。
 *
 * 所有時間一律以「把 local wall-clock 當成 UTC」的方式轉成 epoch ms，
 * 這樣同一份資料在任何時區都會得到完全相同的座標，測試才可重現。
 */

import { isoDateTime, type ISODateTime } from '@bigint-project/core';

import type { TimeScale } from './types.js';

export const MS_PER_DAY = 86_400_000;
export const MS_PER_MINUTE = 60_000;

/** 每個縮放層級的一日寬度（px）。 */
export const PX_PER_DAY: Readonly<Record<TimeScale, number>> = {
  day: 32,
  week: 12,
  month: 4.5,
  quarter: 1.6,
};

/** 拖曳吸附的最小單位（天）。 */
export const SNAP_DAYS: Readonly<Record<TimeScale, number>> = {
  day: 1,
  week: 1,
  month: 1,
  quarter: 7,
};

// ---------------------------------------------------------------------------
// 連續縮放（Ctrl/Cmd + 滾輪）
// ---------------------------------------------------------------------------

/**
 * 連續縮放係數（每日像素）的上下限。
 *
 * 下限對應「一年約數十像素」（0.1 px/day × 365 ≈ 36.5px）；
 * 上限對應「一天約數十像素」（80px/day）。超出範圍即停止縮放，
 * 避免縮到看不見任何刻度，也避免橫向虛擬化要處理過長的 `totalWidth`。
 */
export const MIN_PX_PER_DAY = 0.1;
export const MAX_PX_PER_DAY = 80;

export function clampPxPerDay(pxPerDay: number): number {
  if (!Number.isFinite(pxPerDay)) return MIN_PX_PER_DAY;
  return Math.min(MAX_PX_PER_DAY, Math.max(MIN_PX_PER_DAY, pxPerDay));
}

/**
 * 四個離散刻度依 `PX_PER_DAY` 由細到粗排序，供 {@link scaleForPxPerDay} 使用。
 * 邊界取相鄰兩級 `PX_PER_DAY` 的幾何平均數，讓「表頭該顯示哪個刻度單位」
 * 的切換點落在兩級之間的中點（對數尺度上的中點），而不是任一端。
 */
const SCALE_ORDER: readonly TimeScale[] = ['day', 'week', 'month', 'quarter'];

const SCALE_BOUNDARIES: readonly number[] = SCALE_ORDER.slice(0, -1).map((scale, index) => {
  const next = SCALE_ORDER[index + 1] as TimeScale;
  return Math.sqrt(PX_PER_DAY[scale] * PX_PER_DAY[next]);
});

/**
 * 依目前連續縮放係數（px/day）選出最合適的表頭刻度單位。
 *
 * 縮放本身是連續的（{@link MIN_PX_PER_DAY}–{@link MAX_PX_PER_DAY}）；
 * 這四個離散刻度只決定表頭要顯示「日/週/月/季」哪一種標籤與格線疏密，
 * 不代表縮放只能停在四個點上。
 */
export function scaleForPxPerDay(pxPerDay: number): TimeScale {
  for (let index = 0; index < SCALE_BOUNDARIES.length; index += 1) {
    if (pxPerDay >= (SCALE_BOUNDARIES[index] as number)) {
      return SCALE_ORDER[index] as TimeScale;
    }
  }
  return SCALE_ORDER[SCALE_ORDER.length - 1] as TimeScale;
}

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/** 解析 `YYYY-MM-DDTHH:mm:ss`（wall-clock）為 epoch ms。無法解析時回傳 NaN。 */
export function parseDateTime(value: string): number {
  const match = ISO_RE.exec(value);
  if (match === null) return Number.NaN;
  const [, y, mo, d, h, mi, s] = match;
  return Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    h === undefined ? 0 : Number(h),
    mi === undefined ? 0 : Number(mi),
    s === undefined ? 0 : Number(s),
  );
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/** 將 epoch ms 轉回 `YYYY-MM-DDTHH:mm:ss`。 */
export function formatDateTime(ms: number): ISODateTime {
  const date = new Date(ms);
  return isoDateTime(
    `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(
      date.getUTCDate(),
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`,
  );
}

export const startOfDay = (ms: number): number => Math.floor(ms / MS_PER_DAY) * MS_PER_DAY;

/** 週一為一週之始（與 MS Project 預設一致可由呼叫端調整，此處固定週一）。 */
export function startOfWeek(ms: number): number {
  const day = startOfDay(ms);
  const dow = new Date(day).getUTCDay(); // 0 = Sunday
  const back = (dow + 6) % 7;
  return day - back * MS_PER_DAY;
}

export function startOfMonth(ms: number): number {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
}

export function startOfQuarter(ms: number): number {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3, 1);
}

export function startOfYear(ms: number): number {
  return Date.UTC(new Date(ms).getUTCFullYear(), 0, 1);
}

/** 半年（上半年 = 1 月、下半年 = 7 月）。 */
export function startOfHalfYear(ms: number): number {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() < 6 ? 0 : 6, 1);
}

export const addDays = (ms: number, days: number): number => ms + days * MS_PER_DAY;

export function addMonths(ms: number, count: number): number {
  const date = new Date(ms);
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth() + count,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
}

/** 週六 / 週日視為非工作日。 */
export function isWeekend(ms: number): boolean {
  const dow = new Date(ms).getUTCDay();
  return dow === 0 || dow === 6;
}

// ---------------------------------------------------------------------------
// 座標轉換
// ---------------------------------------------------------------------------

export interface TimeAxis {
  /** 時間軸左緣（含 padding，已對齊該縮放的期間邊界）。 */
  readonly originMs: number;
  /** 時間軸右緣。 */
  readonly endMs: number;
  readonly scale: TimeScale;
  readonly pxPerDay: number;
  /** 整條時間軸的像素寬度。 */
  readonly totalWidth: number;
}

/** 依縮放層級把時間點對齊到期間起點。 */
export function alignToPeriodStart(ms: number, scale: TimeScale): number {
  switch (scale) {
    case 'day':
      return startOfDay(ms);
    case 'week':
      return startOfWeek(ms);
    case 'month':
      return startOfMonth(ms);
    case 'quarter':
      return startOfQuarter(ms);
  }
}

/** 各縮放層級左右預留的天數。 */
const PADDING_DAYS: Readonly<Record<TimeScale, number>> = {
  day: 7,
  week: 14,
  month: 45,
  quarter: 120,
};

/**
 * 由任務日期範圍推導時間軸。空陣列時以 today 為中心給一段預設區間。
 *
 * `pxPerDayOverride` 供連續滾輪縮放使用：`scale` 仍決定表頭刻度單位、
 * 拖曳吸附粒度與左右 padding 天數，但實際每日像素改用這個值，
 * 讓縮放不受四個離散刻度限制。省略時退回 `PX_PER_DAY[scale]`（既有行為）。
 */
export function computeTimeAxis(
  ranges: readonly { readonly startMs: number; readonly finishMs: number }[],
  scale: TimeScale,
  todayMs: number,
  pxPerDayOverride?: number,
): TimeAxis {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const range of ranges) {
    if (Number.isFinite(range.startMs) && range.startMs < min) min = range.startMs;
    if (Number.isFinite(range.finishMs) && range.finishMs > max) max = range.finishMs;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    min = todayMs;
    max = addDays(todayMs, 30);
  }
  const padding = PADDING_DAYS[scale];
  const originMs = alignToPeriodStart(addDays(min, -padding), scale);
  const rawEnd = addDays(max, padding);
  const endMs = Math.max(
    addPeriod(alignToPeriodStart(rawEnd, scale), scale, 1),
    addDays(originMs, 1),
  );
  const pxPerDay =
    pxPerDayOverride === undefined ? PX_PER_DAY[scale] : clampPxPerDay(pxPerDayOverride);
  return {
    originMs,
    endMs,
    scale,
    pxPerDay,
    totalWidth: ((endMs - originMs) / MS_PER_DAY) * pxPerDay,
  };
}

/** 推進一個期間（依縮放層級）。 */
export function addPeriod(ms: number, scale: TimeScale, count: number): number {
  switch (scale) {
    case 'day':
      return addDays(ms, count);
    case 'week':
      return addDays(ms, 7 * count);
    case 'month':
      return addMonths(ms, count);
    case 'quarter':
      return addMonths(ms, 3 * count);
  }
}

export const timeToX = (axis: TimeAxis, ms: number): number =>
  ((ms - axis.originMs) / MS_PER_DAY) * axis.pxPerDay;

export const xToTime = (axis: TimeAxis, x: number): number =>
  axis.originMs + (x / axis.pxPerDay) * MS_PER_DAY;

/** 像素位移換算為天數位移（未吸附）。 */
export const pxToDays = (axis: TimeAxis, dx: number): number => dx / axis.pxPerDay;

/**
 * 四捨五入，且 .5 一律「遠離 0」。
 *
 * `Math.round` 的 .5 固定往 +∞ 走（`Math.round(-0.5) === -0`），會讓吸附有方向性：
 * 往右拖半格前進一格、往左拖半格卻停在原地，來回拖曳無法回到原位。
 */
export function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * 將天數位移吸附到目前縮放的格線。
 * 對稱：`snapDays(axis, -x)` 恆等於 `-snapDays(axis, x)`。
 */
export function snapDays(axis: TimeAxis, days: number): number {
  const unit = SNAP_DAYS[axis.scale];
  return roundHalfAwayFromZero(days / unit) * unit;
}

// ---------------------------------------------------------------------------
// 刻度
// ---------------------------------------------------------------------------

export interface Tick {
  readonly startMs: number;
  readonly endMs: number;
  readonly x: number;
  readonly width: number;
  readonly label: string;
  /** 下層刻度是否落在非工作日（僅 day 層級有意義）。 */
  readonly isNonWorking: boolean;
}

export interface TimelineTicks {
  readonly upper: readonly Tick[];
  readonly lower: readonly Tick[];
}

/**
 * 刻度單位。
 *
 * `month` 與 `monthShort` 邊界相同、只差在標籤：上層需要年份（`2026年2月`），
 * 下層在半年欄底下只需月名（`七月`），與 MS Project 中文版的表頭一致。
 */
type Unit = 'day' | 'week' | 'month' | 'monthShort' | 'quarter' | 'halfYear' | 'year';

const UNIT_START: Readonly<Record<Unit, (ms: number) => number>> = {
  day: startOfDay,
  week: startOfWeek,
  month: startOfMonth,
  monthShort: startOfMonth,
  quarter: startOfQuarter,
  halfYear: startOfHalfYear,
  year: startOfYear,
};

function nextUnit(ms: number, unit: Unit): number {
  switch (unit) {
    case 'day':
      return addDays(ms, 1);
    case 'week':
      return addDays(ms, 7);
    case 'month':
    case 'monthShort':
      return addMonths(ms, 1);
    case 'quarter':
      return addMonths(ms, 3);
    case 'halfYear':
      return addMonths(ms, 6);
    case 'year':
      return addMonths(ms, 12);
  }
}

/** 中文月名（下層月欄使用）。 */
const CHINESE_MONTHS = [
  '一月',
  '二月',
  '三月',
  '四月',
  '五月',
  '六月',
  '七月',
  '八月',
  '九月',
  '十月',
  '十一月',
  '十二月',
] as const;

function labelFor(ms: number, unit: Unit): string {
  const date = new Date(ms);
  switch (unit) {
    case 'day':
      return String(date.getUTCDate());
    case 'week':
      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
    case 'month':
      return `${date.getUTCFullYear()}年${date.getUTCMonth() + 1}月`;
    case 'monthShort':
      return CHINESE_MONTHS[date.getUTCMonth()] ?? '';
    case 'quarter':
      return `${date.getUTCFullYear()} Q${Math.floor(date.getUTCMonth() / 3) + 1}`;
    case 'halfYear':
      return `${date.getUTCFullYear()}年${date.getUTCMonth() < 6 ? '上' : '下'}半年`;
    case 'year':
      return String(date.getUTCFullYear());
  }
}

/** 各縮放層級的（上層, 下層）刻度單位。 */
export const TICK_UNITS: Readonly<Record<TimeScale, readonly [Unit, Unit]>> = {
  day: ['month', 'day'],
  week: ['month', 'week'],
  month: ['halfYear', 'monthShort'],
  quarter: ['year', 'quarter'],
};

/**
 * 產生 [fromMs, toMs) 範圍內的刻度（含跨界的頭尾兩格，避免表頭出現缺口）。
 * 只產生可視範圍的刻度是橫向虛擬化的關鍵。
 */
export function generateTicks(
  axis: TimeAxis,
  fromMs: number,
  toMs: number,
): TimelineTicks {
  const [upperUnit, lowerUnit] = TICK_UNITS[axis.scale];
  return {
    upper: generateUnitTicks(axis, upperUnit, fromMs, toMs),
    lower: generateUnitTicks(axis, lowerUnit, fromMs, toMs),
  };
}

function generateUnitTicks(
  axis: TimeAxis,
  unit: Unit,
  fromMs: number,
  toMs: number,
): Tick[] {
  const clampedFrom = Math.max(fromMs, axis.originMs);
  const clampedTo = Math.min(toMs, axis.endMs);
  const ticks: Tick[] = [];
  if (clampedTo <= clampedFrom) return ticks;

  let cursor = UNIT_START[unit](clampedFrom);
  // 安全上限：避免縮放與範圍不匹配時產生過多節點。
  let guard = 0;
  while (cursor < clampedTo && guard < 20_000) {
    guard += 1;
    const end = nextUnit(cursor, unit);
    const x = timeToX(axis, cursor);
    ticks.push({
      startMs: cursor,
      endMs: end,
      x,
      width: timeToX(axis, end) - x,
      label: labelFor(cursor, unit),
      isNonWorking: unit === 'day' && isWeekend(cursor),
    });
    cursor = end;
  }
  return ticks;
}

/**
 * 可視範圍內的週末色帶。
 *
 * **刻意只在 day 縮放繪製**：week 以上的縮放一天只有 12px 以下，色帶會退化成
 * 整片斑馬紋，反而蓋過任務條。這不是遺漏，改動前請先確認視覺後果。
 */
export function weekendBands(
  axis: TimeAxis,
  fromMs: number,
  toMs: number,
): readonly Tick[] {
  if (axis.scale !== 'day') return [];
  return generateUnitTicks(axis, 'day', fromMs, toMs).filter((tick) => tick.isNonWorking);
}
